import { useCallback, useEffect, useMemo, useRef, useState, type Key } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@nextui-org/react';
import { RangeCalendar } from '@nextui-org/calendar';
import { CtyButton, CtyShowToast, CtySpinner, checkType } from '@circuitry-ai/doc-ui';
import { useGetAiWorkerByIdQuery, useGetEvaluationsPresignedUrlMutation, useTypedSelector } from '@circuitry-ai/doc-data';
import { parseDate, type CalendarDate } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import type { DateValue } from '@internationalized/date';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  PYANALYST_DEFAULT_PRESET,
  PYANALYST_DATE_PRESET_OPTIONS,
  formatPyAnalystDisplayDate,
  getPyAnalystPresetDateRange,
  getPyAnalystPresetOption,
  type PyAnalystDatePresetId,
} from './pyanalystDatePresets';
import {
  fetchPyAnalystPresignedReportUrl,
  getPyAnalystConfigFromAiWorker,
  resolvePyAnalystDateColumn,
} from './pyanalystUtils';

const REPORT_VIEW_MIN_HEIGHT = 'h-[calc(100vh-140px)] min-h-[480px]';

function calendarRange(from: CalendarDate, to: CalendarDate): RangeValue<DateValue> {
  return { start: from, end: to };
}

export function PyanalystDetails() {
  const { t } = useTranslation();
  const aiworkerId = useSearchParams()[0].get('aiworkerId') ?? '';
  const { report: reportFromState } = useLocation().state ?? {};
  const token = useTypedSelector((state) => state.auth.token);

  const { data: analystFromQuery } = useGetAiWorkerByIdQuery(aiworkerId, {
    skip: !aiworkerId || !!reportFromState,
    refetchOnMountOrArgChange: true,
  });

  const endpointConfig = useMemo(
    () => getPyAnalystConfigFromAiWorker(reportFromState ?? analystFromQuery),
    [analystFromQuery, reportFromState]
  );
  const dateColumn = useMemo(() => resolvePyAnalystDateColumn(endpointConfig), [endpointConfig]);

  const defaultRange = useMemo(() => getPyAnalystPresetDateRange(PYANALYST_DEFAULT_PRESET), []);
  const [preset, setPreset] = useState<PyAnalystDatePresetId>(PYANALYST_DEFAULT_PRESET);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [customDraft, setCustomDraft] = useState<RangeValue<DateValue> | null>(null);
  const [isCustomCalendarOpen, setIsCustomCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [presignedUrl, setPresignedUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [getEvaluationsPresignedUrl] = useGetEvaluationsPresignedUrlMutation();
  const hasAutoFetchedRef = useRef(false);

  const fetchReport = useCallback(
    async (range?: { from: CalendarDate; to: CalendarDate }) => {
      const from = range?.from ?? dateFrom;
      const to = range?.to ?? dateTo;

      if (!endpointConfig?.function_url) {
        CtyShowToast(checkType.error, t('PyAnalyst endpoint configuration is missing'));
        return;
      }
      if (!dateColumn) {
        CtyShowToast(checkType.error, t('Date filter column is not configured for this analyst'));
        return;
      }
      if (from.toString() > to.toString()) {
        CtyShowToast(checkType.error, t('Date From should be less than or equal to Date To'));
        return;
      }
      if (!token) {
        CtyShowToast(checkType.error, t('Authentication is not ready yet'));
        return;
      }

      setErrorMessage('');
      setIsLoading(true);
      try {
        const url = await fetchPyAnalystPresignedReportUrl(
          endpointConfig,
          dateColumn,
          from.toString(),
          to.toString(),
          token,
          (body) => getEvaluationsPresignedUrl(body).unwrap()
        );
        setPresignedUrl(url);
      } catch (error) {
        const message =
          (error as Error)?.message || t('Something went wrong while fetching PyAnalyst report');
        setErrorMessage(message);
        CtyShowToast(checkType.error, message);
        setPresignedUrl('');
      } finally {
        setIsLoading(false);
      }
    },
    [dateColumn, dateFrom, dateTo, endpointConfig, getEvaluationsPresignedUrl, t, token]
  );

  useEffect(() => {
    if (!endpointConfig || !dateColumn || hasAutoFetchedRef.current) return;
    hasAutoFetchedRef.current = true;
    void fetchReport();
  }, [endpointConfig, dateColumn, fetchReport]);

  const applyPreset = (nextPreset: Exclude<PyAnalystDatePresetId, 'custom'>) => {
    const { dateFrom: from, dateTo: to } = getPyAnalystPresetDateRange(nextPreset);
    setPreset(nextPreset);
    setDateFrom(from);
    setDateTo(to);
    void fetchReport({ from, to });
  };

  const onPresetSelectionChange = (keys: 'all' | Set<Key>) => {
    const selected = Array.from(keys)[0] as PyAnalystDatePresetId | undefined;
    if (!selected) return;
    if (selected === 'custom') {
      setCustomDraft(calendarRange(dateFrom, dateTo));
      setIsCustomCalendarOpen(true);
      return;
    }
    applyPreset(selected);
  };

  const confirmCustomRange = () => {
    if (!customDraft?.start) {
      CtyShowToast(checkType.error, t('Please select a date range'));
      return;
    }
    const from = parseDate(customDraft.start.toString().slice(0, 10));
    const to = parseDate((customDraft.end ?? customDraft.start).toString().slice(0, 10));
    if (from.toString() > to.toString()) {
      CtyShowToast(checkType.error, t('Date From should be less than or equal to Date To'));
      return;
    }
    setPreset('custom');
    setDateFrom(from);
    setDateTo(to);
    setIsCustomCalendarOpen(false);
    void fetchReport({ from, to });
  };

  const presetOption = getPyAnalystPresetOption(preset);
  const customRangeLabel = `${t('Custom range')} (${formatPyAnalystDisplayDate(dateFrom)} – ${formatPyAnalystDisplayDate(dateTo)})`;
  const presetDisplayLabel = preset === 'custom' ? customRangeLabel : presetOption?.label ?? '';

  if (!endpointConfig && !reportFromState && !analystFromQuery) {
    return (
      <div className={`flex justify-center items-center ${REPORT_VIEW_MIN_HEIGHT}`}>
        <CtySpinner size="lg" />
      </div>
    );
  }

  if (!endpointConfig) {
    return <p className="text-sm text-danger">{t('PyAnalyst endpoint configuration is missing')}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap gap-3 items-end">
        <Select
          size="sm"
          label={t('Date range')}
          labelPlacement="outside"
          selectedKeys={new Set([preset])}
          onSelectionChange={onPresetSelectionChange}
          className="w-full sm:min-w-[280px] sm:max-w-[360px]"
          aria-label={t('Date range')}
          renderValue={() => (
            <span className="text-sm">
              {presetOption?.shortLabel} {presetDisplayLabel}
            </span>
          )}
        >
          {PYANALYST_DATE_PRESET_OPTIONS.map((option) => (
            <SelectItem
              key={option.id}
              textValue={
                option.id === 'custom' && preset === 'custom' ? customRangeLabel : option.label
              }
            >
              <span className="font-medium">{option.shortLabel}</span>
              <span className="text-default-500">
                {' '}
                {option.id === 'custom' && preset === 'custom'
                  ? presetDisplayLabel
                  : option.label}
              </span>
            </SelectItem>
          ))}
        </Select>
        <CtyButton
          id="fetch-pyanalyst-report"
          color="primary"
          className="w-full sm:w-[140px]"
          onPress={() => void fetchReport()}
          isLoading={isLoading}
        >
          {t('View Report')}
        </CtyButton>
      </div>

      <Modal isOpen={isCustomCalendarOpen} onOpenChange={setIsCustomCalendarOpen} size="lg">
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('Select date range')}</ModalHeader>
              <ModalBody className="flex justify-center">
                <RangeCalendar
                  aria-label={t('Custom date range')}
                  value={customDraft}
                  visibleMonths={2}
                  onChange={setCustomDraft}
                />
              </ModalBody>
              <ModalFooter>
                <CtyButton variant="bordered" onPress={() => setIsCustomCalendarOpen(false)}>
                  {t('Cancel')}
                </CtyButton>
                <CtyButton color="primary" onPress={confirmCustomRange}>
                  {t('Apply')}
                </CtyButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {isLoading && !presignedUrl && (
        <div className={`flex justify-center items-center ${REPORT_VIEW_MIN_HEIGHT}`}>
          <CtySpinner size="lg" />
        </div>
      )}

      {!isLoading && errorMessage && <p className="text-sm text-danger">{errorMessage}</p>}

      {presignedUrl && (
        <div className={REPORT_VIEW_MIN_HEIGHT}>
          <iframe src={presignedUrl} title={t('PyAnalyst Report')} className="w-full h-full border-0" />
        </div>
      )}
    </div>
  );
}

export default PyanalystDetails;
